// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React from 'react';
import { BackButton } from '../components/common';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicyPage = ({ onBack }) => {
  const navigate = useNavigate();
  const renderHeader = () => {
    if (onBack) {
      return (
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <BackButton onClick={onBack} label="← More" className="mb-2" />
            <h1 className="text-2xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
        </header>
      );
    }

    return (
      <nav className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 hover:opacity-80 transition"
            >
              <span className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                <span className="hidden sm:inline">PricePAL Compare</span><span className="sm:hidden">PricePAL</span>
              </span>
            </button>
            <button
              onClick={() => navigate('/')}
              className="text-sm sm:text-base text-gray-700 hover:text-blue-600 font-semibold transition whitespace-nowrap"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </nav>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {renderHeader()}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
          {!onBack && <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>}
          <p className="text-gray-600 mb-8">
            <strong>Last Updated:</strong> 11 February 2026<br />
            <strong>Effective Date:</strong> 11 February 2026
          </p>

          <div className="space-y-8 text-gray-700">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="mb-4">
                PricePAL Compare Ltd ("we", "our", "us") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, store, and protect your information in compliance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
              </p>
              <p>
                PricePAL Compare is a price comparison service for UK groceries and fuel. We are registered in the United Kingdom and serve customers primarily in the UK.
              </p>
            </section>

            {/* Data Controller */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Data Controller</h2>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                <p className="font-semibold">PricePAL Compare Ltd</p>
                <p>Company Registration Number: 17031232</p>
                <p>ICO Registration Number: ZC094153</p>
                <p>Data Protection Officer: privacy@pricepalcompare.com</p>
                <p>Address: 20 Wenlock Road, London, N1 7GU</p>
              </div>
              <p>
                We are responsible for deciding how we hold and use personal information about you. We are required under data protection legislation to notify you of the information contained in this Privacy Policy.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Information You Provide</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, password</li>
                <li><strong>Shopping Data:</strong> Product searches, price comparisons, shopping lists</li>
                <li><strong>Payment Information:</strong> Payment card details (processed securely by Stripe — we do not store full card details)</li>
                <li><strong>Fuel Savings:</strong> Fuel purchase records, retailer loyalty programs</li>
                <li><strong>Communications:</strong> Messages you send to customer support</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Information Automatically Collected</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent</li>
                <li><strong>Location Data:</strong> Approximate location (city/region level only)</li>
                <li><strong>Cookies:</strong> See our Cookie Policy for details</li>
              </ul>
            </section>

            {/* How We Use Your Data */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. How We Use Your Data</h2>
              <p className="mb-4">We process your personal data for the following purposes:</p>
              
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-bold text-gray-900 mb-2">4.1 Service Provision (Contract Performance)</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Provide price comparison services</li>
                    <li>Manage your account and subscription</li>
                    <li>Process payments</li>
                    <li>Send service-related notifications</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-bold text-gray-900 mb-2">4.2 Service Improvement (Legitimate Interest)</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Analyze usage patterns to improve our service</li>
                    <li>Develop new features</li>
                    <li>Conduct research and analytics</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-bold text-gray-900 mb-2">4.3 Marketing (Consent)</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Send promotional emails (only with your consent)</li>
                    <li>Personalized recommendations</li>
                    <li>Special offers and discounts</li>
                  </ul>
                  <p className="mt-2 text-sm text-gray-600">You can opt out of marketing communications at any time.</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-bold text-gray-900 mb-2">4.4 Legal Compliance</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Comply with legal obligations</li>
                    <li>Prevent fraud and abuse</li>
                    <li>Enforce our terms and conditions</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Data Sharing */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. How We Share Your Data</h2>
              <p className="mb-4">
                <strong>We do NOT sell your personal data to third parties.</strong>
              </p>
              <p className="mb-4">We only share your data with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Service Providers:</strong> Payment processor (Stripe), cloud hosting, email services (IONOS SMTP), analytics (PostHog) — only as necessary to provide our services</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our legal rights</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets (you will be notified)</li>
              </ul>
              <p className="mt-4 bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <strong>Important:</strong> We never share your shopping data with retailers or advertisers.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights Under UK GDPR</h2>
              <p className="mb-4">You have the following rights:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">✓ Right to Access</h4>
                  <p className="text-sm">Request a copy of your personal data</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">✓ Right to Rectification</h4>
                  <p className="text-sm">Correct inaccurate data</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">✓ Right to Erasure</h4>
                  <p className="text-sm">Request deletion of your data</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">✓ Right to Restriction</h4>
                  <p className="text-sm">Limit how we use your data</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">✓ Right to Portability</h4>
                  <p className="text-sm">Receive your data in a portable format</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">✓ Right to Object</h4>
                  <p className="text-sm">Object to certain types of processing</p>
                </div>
              </div>
              <p className="mt-4">
                <strong>To exercise your rights:</strong> Email privacy@pricepalcompare.com with your request. We will respond within 30 days.
              </p>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Security</h2>
              <p className="mb-4">We implement industry-standard security measures:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>SSL/TLS encryption for all data transmission</li>
                <li>Encrypted data storage</li>
                <li>Regular security audits and penetration testing</li>
                <li>Access controls and authentication</li>
                <li>Secure payment processing via PCI-DSS compliant providers</li>
                <li>Regular staff training on data protection</li>
              </ul>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Data Retention</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Active Accounts:</strong> Data retained while your account is active</li>
                <li><strong>Closed Accounts:</strong> Data deleted within 90 days (unless legal obligation requires retention)</li>
                <li><strong>Transaction Records:</strong> Retained for 7 years (UK tax law requirement)</li>
                <li><strong>Marketing Data:</strong> Deleted immediately upon opt-out</li>
              </ul>
            </section>

            {/* International Transfers */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. International Data Transfers</h2>
              <p>
                Your data is primarily stored in the UK. If we transfer data outside the UK, we ensure adequate safeguards are in place through:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>EU Standard Contractual Clauses</li>
                <li>Adequacy decisions by the UK government</li>
                <li>Other appropriate safeguards approved by the ICO</li>
              </ul>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
              <p className="bg-red-50 border-l-4 border-red-500 p-4">
                <strong>Age Restriction:</strong> PricePAL Compare is not intended for children under 16. We do not knowingly collect data from children. If you are under 16, please do not use our service or provide any personal information.
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Cookies</h2>
              <p>
                We use cookies to improve your experience. For detailed information, please see our{' '}
                <button 
                  onClick={() => navigate('/cookies')}
                  className="text-blue-600 hover:underline font-semibold"
                >
                  Cookie Policy
                </button>.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of significant changes via email or prominent notice on our website. Continued use of our service after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Complaints */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. How to Complain</h2>
              <p className="mb-4">
                If you have concerns about how we handle your data, please contact us first at privacy@pricepalcompare.com
              </p>
              <p>
                You also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO):
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                <p className="font-semibold">Information Commissioner's Office</p>
                <p>Wycliffe House, Water Lane</p>
                <p>Wilmslow, Cheshire SK9 5AF</p>
                <p>Phone: 0303 123 1113</p>
                <p>Website: <a href="https://ico.org.uk" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">ico.org.uk</a></p>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-xl p-8">
              <h2 className="text-2xl font-bold mb-4">Contact Us</h2>
              <p className="mb-4">
                For any questions about this Privacy Policy or our data practices:
              </p>
              <div className="space-y-2">
                <p><strong>Email:</strong> privacy@pricepalcompare.com</p>
                <p><strong>Data Protection Officer:</strong> dpo@pricepalcompare.com</p>
                <p><strong>Address:</strong> 20 Wenlock Road, London, N1 7GU</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;